/**
 * Microsoft Advertising Integration Module (Story 19.1)
 *
 * Barrel export for OAuth, REST helpers, errors, and the Microsoft Ads adapter.
 *
 * Microsoft Ads characteristics worth knowing about before reading this code:
 *  - OAuth 2.0 + PKCE against the Microsoft identity platform v2 endpoint
 *  - Two credentials per data API call: OAuth Bearer + universal DeveloperToken
 *  - Composite provider_account_id: `"{customerId}:{accountId}"`
 *  - Multi-customer / multi-account: a single OAuth grant may surface dozens
 *    of CustomerId/AccountId pairs — the OAuth callback persists ALL of them
 *    in `integrations.metadata.allCustomerAccounts`.
 *
 * The folder is named `microsoft/` (not `microsoft-ads/`) so future Microsoft
 * properties (Bing Search, Microsoft Clarity, etc.) can live alongside the
 * Ads adapter, mirroring how `meta/` houses both Facebook and Instagram.
 */

import { registerAdapter } from "../registry";
import { microsoftAdsAdapter } from "./microsoft-ads";

// Register the adapter so getAdapter("microsoft-ads") returns it.
registerAdapter("microsoft-ads", microsoftAdsAdapter);

// Platform values (shared constant for frontend/backend alignment, mirrors klaviyo)
export const MICROSOFT_ADS_PLATFORM_VALUES = ["microsoft-ads"] as const;
export type MicrosoftAdsPlatform =
  (typeof MICROSOFT_ADS_PLATFORM_VALUES)[number];

// OAuth utilities
export {
  buildMicrosoftAdsAuthUrl,
  exchangeMicrosoftAdsCode,
  refreshMicrosoftAdsToken,
  parseMicrosoftAdsCallbackError,
  MICROSOFT_ADS_SCOPES,
  MICROSOFT_ADS_REFRESH_TOKEN_LIFETIME_MS,
  type MicrosoftAdsAuthUrlOptions,
  type MicrosoftAdsTokenResponse,
  type MicrosoftAdsCallbackError,
} from "./oauth";

// REST helpers
export {
  buildMicrosoftAdsHeaders,
  fetchMicrosoftAdsUserInfo,
  fetchMicrosoftAdsAccountsSearch,
  MICROSOFT_ADS_API_BASE,
  type MicrosoftAdsCustomerRole,
  type MicrosoftAdsUserInfo,
  type MicrosoftAdsAdvertiserAccount,
} from "./api";

// Error parsing
export {
  parseMicrosoftAdsApiError,
  microsoftAdsErrorTypeToAdapterCode,
  MicrosoftAdsApiCallError,
  type MicrosoftAdsApiError,
  type MicrosoftAdsErrorType,
} from "./errors";

// Adapter
export {
  microsoftAdsAdapter,
  MICROSOFT_ADS_METRICS,
  MICROSOFT_ADS_DIMENSIONS,
  MICROSOFT_ADS_API_VERSION,
  MAX_FETCHMETRICS_DAYS,
  buildMicrosoftAdsCacheKey,
} from "./microsoft-ads";

// Reporting Service v13 — Submit / Poll / Download / Unzip / Parse (Story 19.2)
export {
  MICROSOFT_ADS_REPORTING_API_BASE,
  POLL_INTERVAL_MS,
  MAX_POLL_ATTEMPTS,
  SUBMIT_TIMEOUT_MS,
  POLL_TIMEOUT_MS,
  DOWNLOAD_TIMEOUT_MS,
  REPORT_TYPE_TO_API,
  submitMicrosoftAdsReport,
  pollMicrosoftAdsReport,
  pollMicrosoftAdsReportUntilDone,
  downloadMicrosoftAdsReportZip,
  downloadAndExtractCsv,
  parseMicrosoftAdsCsv,
  type MicrosoftAdsReportType,
  type MicrosoftAdsReportStatus,
  type ReportSubmitResponse,
  type ReportPollResponse,
} from "./reporting";

// Report-request body builders (Story 19.2)
export {
  MICROSOFT_ADS_DEPRECATED_COLUMNS,
  MAX_DATE_RANGE_DAYS,
  COLUMNS_BY_REPORT_TYPE,
  OVERVIEW_COLUMNS,
  CAMPAIGNS_COLUMNS,
  AD_GROUPS_COLUMNS,
  KEYWORDS_COLUMNS,
  SEARCH_QUERIES_COLUMNS,
  SHOPPING_COLUMNS,
  AGE_GENDER_COLUMNS,
  GEO_COLUMNS,
  buildSubmitBody,
  dateToYearMonthDay,
  assertNoDeprecatedColumns,
  assertNoColumnConflicts,
} from "./report-builders";

// Row transformers (Story 19.2)
export {
  parseMetric,
  parsePercent,
  parseQualityScoreEnum,
  parseDuration,
  transformOverviewRows,
  transformCampaignsRows,
  transformAdGroupsRows,
  transformKeywordsRows,
  transformSearchQueriesRows,
  transformShoppingRows,
  transformAgeGenderRows,
  transformGeoRows,
  TRANSFORMERS,
} from "./transformers";

// ZIP extractor (Story 19.2)
export { unzipSingleFileToText } from "./unzip";
